import type { quoteLineType } from '../../types'

const CATEGORY_FIELDS = [
  'ProductCategory',
  'ProductCategorySource',
  'ProductCategoryConfidence',
  'ProductCategoryKey',
  'ProductCategoryAttempts',
  'ProductCategoryAttemptAt',
] as const

type LineWithChildren = quoteLineType & { children?: quoteLineType[] }

function flatten(lines: LineWithChildren[]): quoteLineType[] {
  return lines.flatMap(line => [line, ...(line.children ?? [])])
}

const samePart = (a: quoteLineType, b: quoteLineType): boolean =>
  (['MfrPartNumber', 'Manufacturer'] as const).every(
    field =>
      String(a[field] ?? '')
        .trim()
        .toUpperCase() ===
      String(b[field] ?? '')
        .trim()
        .toUpperCase(),
  )

// Merge only category fields; skip lines the user re-picked or whose part changed
export function mergeCategoryFields<T extends LineWithChildren>(current: T[], fromServer: LineWithChildren[]): T[] {
  const serverById = new Map(flatten(fromServer).map(line => [line.LineItemId, line]))
  const merge = (line: quoteLineType): quoteLineType => {
    const server = serverById.get(line.LineItemId)
    if (!server || line.ProductCategorySource === 'MANUAL' || !samePart(server, line)) return line
    const merged: quoteLineType = { ...line }
    for (const field of CATEGORY_FIELDS) Object.assign(merged, { [field]: server[field] })
    if (!line.ProductType && server.ProductType) merged.ProductType = server.ProductType
    return merged
  }
  return current.map(line => ({
    ...(merge(line) as T),
    ...(line.children ? { children: line.children.map(merge) } : {}),
  }))
}
